/**
 * The Meal Filter List page's own check of meals against a filter, on the data meals/filter-data
 * returns for each meal: nutrients by symbol at each size (as the published catalog carries
 * them) and its foods with their tag ids. The server's checks (meals/nutrients, v2/restrictions)
 * judge the medium size; here every size is judged, and valid/problem follows the medium one.
 */
export const MEAL_SIZES = ['small', 'medium', 'large'];
export const JUDGED_SIZE = 'medium';
// a filter's limits are a day's, over three meals (fed-lib personal-menu-meal-filters.js)
const MEALS_A_DAY = 3;

const number = value => (value === '' || value === null || value === undefined ? null : Number(value));

/**
 * the targets worth checking, with the nutrient's symbol and unit and the limits for one meal:
 * a day's limits are split over three meals; limits given per meal (isDailyTarget off) stay
 */
export function mealTargets(targets, nutrientsById = {}, isDailyTarget = true) {
  const perMeal = limit => (isDailyTarget ? limit / MEALS_A_DAY : limit);
  return (targets || [])
    .filter(target => target.nutrient_id && (number(target.lower_limit) || number(target.upper_limit) || number(target.target)))
    .map(target => {
      const nutrient = nutrientsById[target.nutrient_id] || {};
      return {
        nutrient_id: target.nutrient_id,
        symbol: target.nutrient_symbol || nutrient.nutrientsymbol || null,
        unit: nutrient.unit || '',
        lowerLimit: number(target.lower_limit) ? perMeal(number(target.lower_limit)) : null,
        upperLimit: number(target.upper_limit) ? perMeal(number(target.upper_limit)) : null,
      };
    });
}

/** the restricted tag ids of each chosen restriction, from the substitutions map */
export function restrictedTagsOf(restrictionIds, restrictions = {}) {
  return Object.fromEntries((restrictionIds || []).map(id => [id, new Set(((restrictions[id] || {}).restrictedTags || []).map(Number))]));
}

function checkSize(nutrients, targets) {
  return targets.map(target => {
    const amount = nutrients && target.symbol ? nutrients[target.symbol] : undefined;
    const result = {target, amount: amount === undefined ? null : amount, unit: target.unit, problem: null};
    if (amount === undefined || amount === null) {
      result.problem = {error: 'not found'};
      return result;
    }
    if (target.lowerLimit && amount < target.lowerLimit) result.problem = {lower_limit: target.lowerLimit};
    if (target.upperLimit && amount > target.upperLimit) result.problem = {upper_limit: target.upperLimit, ...result.problem};
    return result;
  });
}

/**
 * one meal's results as the page shows them: the judged size's result by nutrient id, whether
 * each size meets every target, the restricted foods' names by restriction, and the problems.
 * A meal whose data has not arrived is not checked (sizes null, no problems).
 */
export function checkMealData(data, targets, restrictedTags = {}) {
  if (!data) {
    return {
      targets: {},
      sizes: Object.fromEntries(MEAL_SIZES.map(size => [size, null])),
      restriction: {},
      hasProblem: {targets: false, restrictions: false},
      checked: false,
    };
  }
  const bySize = Object.fromEntries(MEAL_SIZES.map(size => [size, checkSize((data.nutrients || {})[size], targets)]));
  const sizes = Object.fromEntries(MEAL_SIZES.map(size => [size, bySize[size].every(item => !item.problem)]));
  const judged = bySize[JUDGED_SIZE];
  const restriction = {};
  for (const [restrictionId, tagIds] of Object.entries(restrictedTags)) {
    const foods = (data.foods || []).filter(food => (food.tagIds || []).some(tagId => tagIds.has(Number(tagId))));
    if (foods.length) restriction[restrictionId] = foods.map(food => food.name);
  }
  return {
    targets: Object.fromEntries(judged.map(item => [item.target.nutrient_id, item])),
    sizes,
    restriction,
    hasProblem: {targets: judged.some(item => !!item.problem), restrictions: Object.keys(restriction).length > 0},
    checked: true,
  };
}
